/* @flow */
/**
 * Affiliate Typename: the possible typenames for a group.
 * The types contained in this file should not be changed lightly as they are stored on the CMR (kwanza) and user by other software (e.g. Manatee) which expects them to look a certain way.
 * NB: documentation.js can't parse `|` ([see issue](https://github.com/documentationjs/documentation/issues/589)). Here's the raw type:
 * ```
 * type AffiliateTypename = 'application' | 'flow' | 'user'
 * ```
 * @typedef { string } AffiliateTypename
 * @memberof GroupService
 */

import Typenames from './Typenames'
import { ActionTypes, store } from 'store'
import _ from 'lodash'
type AffiliateTypename = 'application' | 'flow' | 'user'

/**
 * Group: keeps applications, flows and users under the same umbrella.
 * @typedef { Object } Group
 * @memberof GroupService
 */
export type Group = {
  id?: string,
  name: string,
  key?: string,
  version?: string,
  computedName?: string,
  computedDomain?: string,
  description?: string,
  color?: string,
  application?: Array<string>,
  user?: Array<string>,
  flow?: Array<string>,
  group?: Array<string>,
  subject?: Array<string>,
  disableAutosave?: boolean,
  disableFlowsEditing?: boolean,
  promptOnAdd?: boolean,
  promptOnAddMsg?: string,
  promptOnRemove?: boolean,
  promptOnRemoveMsg?: string
}

const defaultColor = '#f2f2f2' // light grey,
const defaultDescriptive = Object.freeze({
  description: '',
  key: '',
  color: defaultColor,
  application: [],
  user: [],
  flow: [],
  group: [],
  disableAutosave: false,
  disableFlowsEditing: false,
  promptOnAdd: false,
  promptOnAddMsg: '',
  promptOnRemove: false,
  promptOnRemoveMsg: ''
})

/**
 * Group typename
 * @private
 * @memberof GroupService
 */
const TYPENAME = Typenames.GROUP

const uniqueConstraints = Object.freeze(['name'])

/**
 * Creates a new group.
 * @function
 * @memberof GroupService
 *
 * @return { Promise<string> } The id of the added group.
 */
const createGroup = (group: Group): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.CREATE, {
    typename: TYPENAME,
    item: group,
    uniqueConstraints: uniqueConstraints
  })
}

/**
 * Reads a group, flattening 'descriptive'.
 * @function
 * @memberof GroupService
 *
 * @return { Promise<Group> } The group.
 */
const readGroup = (id: string): Promise<Object> => {
  return store.dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id }).then(field => {
    return { ...defaultDescriptive, ...field }
  })
}

/**
 * Updates a group.
 * @function
 * @memberof GroupService
 *
 * @return { Promise<any> }
 */
const updateGroup = (id: string, group: Group): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.UPDATE, {
    typename: TYPENAME,
    id: id,
    item: group,
    uniqueConstraints: uniqueConstraints
  })
}

/**
 * Deletes a group.
 * @function
 * @memberof GroupService
 *
 * @return { Promise<any> }
 */
const deleteGroup = (id: string): Promise<Object> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all groups flattening descriptive.
 * @function
 * @memberof GroupService
 *
 * @return { Observable<Array<Group>> } All the groups.
 */
const readAllGroups = (): Promise<Object> => {
  return store.dispatch(ActionTypes.GET, TYPENAME).then(groups => {
    return _.map(groups, g => {
      return {
        ...defaultDescriptive,
        ...g
      }
    })
  })
}

/**
 * Reads all groups that the flow belongs to, flattening descriptive.
 * @function
 * @memberof FlowService
 *
 * @return { Observable<Array<Group>> } Groups that the flow belongs to.
 */
const readGroupsForFlow = (flowId: string): Promise<Array<Group>> => {
  return store.dispatch(ActionTypes.GET_FOR_FLOW, { typename: TYPENAME, flowId: flowId })
}

/**
 * Copies a group.
 * @function
 * @memberof GroupService
 *
 * @return { Promise<string> }
 */
const copyGroup = (id: string, nameSuffix: string): Promise<PostResponse> => {
  return self.readGroup(id).then(group => {
    const groupToCopy = { ...group, name: `${group.name}${nameSuffix}` }
    delete groupToCopy.id

    return self.createGroup(groupToCopy)
  })
}

/**
 * Updates group affiliates, adding a new affiliate if it doesn't exist.
 * @function
 * @memberof GroupService
 *
 * @return { Promise<Object> } An object containing altered `affiliateTypename`, `groupId` & `affiliateId`.
 */
const addAffiliate = (
  affiliateTypename: AffiliateTypename,
  groupId: string,
  affiliateId: string
): Promise<Object> => {
  return store.dispatch(ActionTypes.ADD_AFFILIATE, {
    typename: TYPENAME,
    affiliateTypename: affiliateTypename,
    id: groupId,
    affiliateId: affiliateId
  })
}

/**
 * Updates group affiliates, removing an affiliation.
 * @function
 * @memberof GroupService
 *
 * @return { Promise<Object> } An object containing altered `affiliateTypename`, `groupId` & `affiliateId`.
 */
const removeAffiliate = (
  affiliateTypename: AffiliateTypename,
  groupId: string,
  affiliateId: string
): Promise<Object> => {
  return store.dispatch(ActionTypes.REMOVE_AFFILIATE, {
    typename: TYPENAME,
    affiliateTypename: affiliateTypename,
    id: groupId,
    affiliateId: affiliateId
  })
}

const self = {
  createGroup,
  readGroup,
  updateGroup,
  deleteGroup,
  readAllGroups,
  copyGroup,
  addAffiliate,
  removeAffiliate,
  defaultDescriptive,
  defaultColor,
  readGroupsForFlow
}

/**
 * Group service: CRUD interface for groups.
 * @module GroupService
 * @namespace GroupService
 * @version 1.0.0
 */
export default self
